import { useAuth } from '@/auth/auth-provider'
import { useDealership } from '@/contexts/DealershipContext'
import type { DealershipEntity, DealershipGroup } from '@/types'
import type { EvalContactChannel, EvaluationRunRow, EvaluationRunSummary } from '@/pages/admin/types/EvaluationTypes'
import {
  formatPersonaName,
  inferEvalModeFromRows,
  rowsToEvalJobs,
  type EvalJobView,
  type EvalMode,
} from '@/pages/admin/utils/evaluationRunMappers'
import { judgeEvaluationApi } from '@/services/judgeEvaluation.api'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  Grid,
  Group,
  Loader,
  NumberInput,
  Progress,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { IconCheck, IconChevronDown, IconChevronUp, IconHistory, IconRefresh, IconScale, IconX } from '@tabler/icons-react'
import { useCallback, useEffect, useRef, useState } from 'react'

interface EvaluationSettings {
  count: number
  dealerId: string
  channels: Record<EvalContactChannel, boolean>
}

const DEFAULT_CHANNELS: Record<EvalContactChannel, boolean> = { sms: true, email: true }

function selectedContactChannels(channels: Record<EvalContactChannel, boolean>): EvalContactChannel[] {
  const selected: EvalContactChannel[] = []
  if (channels.sms) selected.push('sms')
  if (channels.email) selected.push('email')
  return selected
}

function formatRunLabel(run: EvaluationRunSummary): string {
  const when = new Date(run.createdAt).toLocaleString()
  const type = run.evalType === 'vehicle-eval' ? 'Vehicle' : 'Conversation'
  return `${when} · ${type} · ${run.rowCount} result${run.rowCount === 1 ? '' : 's'} · avg ${run.averageScore.toFixed(1)}`
}

function defaultDealerIdFromContext(selectedEntity: DealershipEntity | null): string {
  if (!selectedEntity) return ''
  if (selectedEntity.type === 'dealership') return selectedEntity.id
  if (selectedEntity.type === 'group') {
    const group = selectedEntity as DealershipGroup
    return group.dealerships[0]?.id ?? ''
  }
  return ''
}

async function pollVehicleRun(runId: string, maxAttempts = 90): Promise<EvaluationRunRow[]> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const rows = await judgeEvaluationApi.getRunResults(runId)
    if (rows.length >= 2) {
      return rows
    }
    await new Promise(resolve => setTimeout(resolve, 10000))
  }
  return judgeEvaluationApi.getRunResults(runId)
}

export function JudgeEvaluation() {
  const { entities, selectedEntity } = useDealership()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [mode, setMode] = useState<EvalMode>('conversation')
  const [personaIds, setPersonaIds] = useState<string[]>([])
  const [settings, setSettings] = useState<EvaluationSettings>({
    count: 1,
    dealerId: '',
    channels: { ...DEFAULT_CHANNELS },
  })
  const [isRunning, setIsRunning] = useState(false)
  const [jobs, setJobs] = useState<EvalJobView[]>([])
  const [resultsDisplayMode, setResultsDisplayMode] = useState<EvalMode>('conversation')
  const [showResults, setShowResults] = useState(false)
  const [progress, setProgress] = useState({ completed: 0, total: 0 })
  const [expandedSections, setExpandedSections] = useState<Record<string, { justification: boolean; subscores: boolean }>>({})
  const [pastRuns, setPastRuns] = useState<EvaluationRunSummary[]>([])
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null)
  const [loadingPastRuns, setLoadingPastRuns] = useState(false)
  const [loadingRunDetails, setLoadingRunDetails] = useState(false)
  const [viewingHistorical, setViewingHistorical] = useState(false)
  const roundRobinIndexRef = useRef(0)

  const applyRunRows = useCallback((rows: EvaluationRunRow[], runId: string) => {
    const historicalMode = inferEvalModeFromRows(rows)
    setResultsDisplayMode(historicalMode)
    setJobs(rowsToEvalJobs(rows, historicalMode))
    setShowResults(true)
    setViewingHistorical(true)
    setSelectedRunId(runId)
    setProgress({ completed: rows.length, total: rows.length })
  }, [])

  const fetchPastRuns = useCallback(
    async (dealerId: string, options?: { autoLoadLatest?: boolean }) => {
      if (!dealerId || !isAuthenticated || authLoading) {
        setPastRuns([])
        return
      }
      setLoadingPastRuns(true)
      try {
        const evalType = mode === 'vehicle' ? 'vehicle-eval' : 'conversation'
        const runs = await judgeEvaluationApi.listRuns(dealerId, evalType)
        setPastRuns(runs)

        if (options?.autoLoadLatest && runs.length > 0) {
          const latest = runs[0]!
          setLoadingRunDetails(true)
          try {
            const rows = await judgeEvaluationApi.getRunResults(latest.runId)
            applyRunRows(rows, latest.runId)
          } catch {
            // List loaded; details failed — user can pick another run
          } finally {
            setLoadingRunDetails(false)
          }
        }
      } catch {
        setPastRuns([])
      } finally {
        setLoadingPastRuns(false)
      }
    },
    [mode, isAuthenticated, authLoading, applyRunRows],
  )

  const loadPastRuns = useCallback(
    () => fetchPastRuns(settings.dealerId, { autoLoadLatest: false }),
    [fetchPastRuns, settings.dealerId],
  )

  const loadHistoricalRun = useCallback(
    async (runId: string) => {
      if (!isAuthenticated || authLoading) return
      setLoadingRunDetails(true)
      try {
        const rows = await judgeEvaluationApi.getRunResults(runId)
        applyRunRows(rows, runId)
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to load run results')
      } finally {
        setLoadingRunDetails(false)
      }
    },
    [isAuthenticated, authLoading, applyRunRows],
  )

  // Default dealership from global context (same as other admin tools)
  useEffect(() => {
    if (settings.dealerId) return
    const id = defaultDealerIdFromContext(selectedEntity)
    if (id) setSettings(prev => ({ ...prev, dealerId: id }))
  }, [selectedEntity, settings.dealerId])

  // Load saved runs and show the latest when dealership or scenario changes
  useEffect(() => {
    if (!settings.dealerId || !isAuthenticated || authLoading) return
    void fetchPastRuns(settings.dealerId, { autoLoadLatest: true })
  }, [settings.dealerId, mode, isAuthenticated, authLoading, fetchPastRuns])

  useEffect(() => {
    if (!isAuthenticated || authLoading) return

    const loadPool = async () => {
      try {
        if (mode === 'conversation') {
          const data = await judgeEvaluationApi.getPersonas()
          setPersonaIds(data.map(p => p.id))
        } else {
          const data = await judgeEvaluationApi.getVehicleEvalPersonas()
          setPersonaIds(data.map(p => p.id))
        }
      } catch {
        setPersonaIds([])
      }
    }
    void loadPool()
  }, [mode, isAuthenticated, authLoading])

  const getAllDealerships = () => {
    const dealerships: Array<{ value: string; label: string }> = []
    entities.forEach(entity => {
      if (entity.type === 'group') {
        const group = entity as { dealerships?: Array<{ id: string; name: string }>; name: string }
        group.dealerships?.forEach(dealer => {
          dealerships.push({ value: dealer.id, label: `${dealer.name} (${group.name})` })
        })
      } else if (entity.type === 'dealership') {
        const dealer = entity as { id: string; name: string }
        dealerships.push({ value: dealer.id, label: dealer.name })
      }
    })
    return dealerships.sort((a, b) => a.label.localeCompare(b.label))
  }

  const personaIdAt = (startIndex: number, offset: number): string => {
    if (personaIds.length === 0) return 'unknown'
    return personaIds[(startIndex + offset) % personaIds.length]!
  }

  const updateJob = (id: string, patch: Partial<EvalJobView>) => {
    setJobs(prev => prev.map(j => (j.id === id ? { ...j, ...patch } : j)))
  }

  const runConversationJob = async (jobId: string, personaStartIndex: number, dealershipId: number) => {
    updateJob(jobId, { status: 'running' })
    try {
      const response = await judgeEvaluationApi.runEvaluation({
        dealershipId,
        count: 1,
        personaStartIndex,
        contactChannels: selectedContactChannels(settings.channels),
      })
      const result = response.results[0]
      if (!result) {
        updateJob(jobId, { status: 'error', error: 'No result returned' })
        return
      }
      updateJob(jobId, {
        status: result.judgeResult ? 'success' : 'error',
        personaId: result.persona.id,
        personaLabel: formatPersonaName(result.persona.id),
        error: result.judgeResult ? undefined : 'Judge did not return a score',
        trustScore: result.judgeResult?.overallScore,
        outcomeAttainment: result.outcome?.outcomeAttainment ?? result.conversation.outcomeAttainment ?? null,
        escalated: result.conversation.escalated,
        appointmentScheduled: result.conversation.appointmentScheduled ?? result.conversation.appointmentCompleted,
        messageCount: result.conversation.messageCount,
        customerName: result.customer.name,
        customerId: result.customer.customerId,
        judgeJustification: result.judgeResult?.employee.justification,
        subscores: result.judgeResult?.employee.subscores,
        runId: response.runId,
      })
    } catch (err) {
      updateJob(jobId, {
        status: 'error',
        error: err instanceof Error ? err.message : 'Evaluation failed',
      })
    }
  }

  const runVehicleJob = async (jobId: string, personaStartIndex: number, dealershipId: number, expectedPersonaId: string) => {
    updateJob(jobId, { status: 'running' })
    try {
      const started = await judgeEvaluationApi.runVehicleEval({
        dealershipId,
        count: 1,
        personaStartIndex,
        contactChannels: selectedContactChannels(settings.channels),
      })
      updateJob(jobId, { runId: started.runId })
      const rows = await pollVehicleRun(started.runId)
      const personaRows = rows.filter(r => r.personaId === expectedPersonaId)
      const general = personaRows.find(r => r.evalType === 'vehicle-eval-general')
      const recommendation = personaRows.find(r => r.evalType === 'vehicle-eval-recommendation')
      const primary = recommendation || general || personaRows[0]
      updateJob(jobId, {
        status: primary ? 'success' : 'error',
        error: primary ? undefined : 'No vehicle eval rows found',
        trustScore: primary?.overallScore,
        messageCount: primary?.messageCount,
        customerName: primary?.customerName ?? undefined,
        customerId: primary?.customerId ?? undefined,
        appointmentScheduled: primary?.appointmentCompleted,
        vehicleRows: personaRows.length > 0 ? personaRows : rows,
      })
    } catch (err) {
      updateJob(jobId, {
        status: 'error',
        error: err instanceof Error ? err.message : 'Vehicle evaluation failed',
      })
    }
  }

  const handleRunEvaluation = async () => {
    if (!settings.dealerId) {
      alert('Please select a dealership')
      return
    }

    const contactChannels = selectedContactChannels(settings.channels)
    if (contactChannels.length === 0) {
      alert('Select at least one channel (SMS or Email)')
      return
    }

    const dealershipId = parseInt(settings.dealerId, 10)
    const count = Math.max(1, settings.count)
    const startIndex = roundRobinIndexRef.current

    const initialJobs: EvalJobView[] = Array.from({ length: count }, (_, i) => {
      const pid = personaIdAt(startIndex, i)
      return {
        id: crypto.randomUUID(),
        personaId: pid,
        personaLabel: formatPersonaName(pid),
        status: 'queued',
      }
    })

    roundRobinIndexRef.current = personaIds.length > 0 ? (startIndex + count) % personaIds.length : startIndex + count

    setIsRunning(true)
    setShowResults(true)
    setViewingHistorical(false)
    setSelectedRunId(null)
    setResultsDisplayMode(mode)
    setJobs(initialJobs)
    setProgress({ completed: 0, total: count })

    let completed = 0
    const markComplete = () => {
      completed += 1
      setProgress({ completed, total: count })
    }

    try {
      for (let i = 0; i < initialJobs.length; i++) {
        const job = initialJobs[i]!
        const personaStartIndex = startIndex + i
        try {
          if (mode === 'conversation') {
            await runConversationJob(job.id, personaStartIndex, dealershipId)
          } else {
            await runVehicleJob(job.id, personaStartIndex, dealershipId, job.personaId)
          }
        } finally {
          markComplete()
        }
      }
    } finally {
      setIsRunning(false)
      void fetchPastRuns(settings.dealerId, { autoLoadLatest: false })
    }
  }

  const toggleSection = (jobId: string, section: 'justification' | 'subscores') => {
    setExpandedSections(prev => ({
      ...prev,
      [jobId]: {
        ...prev[jobId],
        [section]: !prev[jobId]?.[section],
      },
    }))
  }

  const successCount = jobs.filter(j => j.status === 'success').length
  const errorCount = jobs.filter(j => j.status === 'error').length
  const inFlightCount = jobs.filter(j => j.status === 'running' || j.status === 'queued').length
  const scoredJobs = jobs.filter(j => j.trustScore != null)
  const avgTrust = scoredJobs.reduce((sum, j) => sum + (j.trustScore ?? 0), 0) / (scoredJobs.length || 1)

  return (
    <Stack gap="sm">
      <Card withBorder padding="md">
        <Stack gap="sm">
          <div>
            <Title order={4}>AI automated testing</Title>
            <Text size="xs" c="dimmed" mt={4}>
              {mode === 'conversation'
                ? 'Simulated buyer conversations scored for trust, steering, and outcome. Personas rotate automatically (round-robin).'
                : 'Vehicle-grounded evals with inventory checks. Personas rotate automatically (round-robin).'}
              {personaIds.length > 0 && ` ${personaIds.length} profiles in pool.`}
            </Text>
          </div>

          <Grid gutter="md">
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Select
                label="Dealership"
                placeholder="Select dealership"
                value={settings.dealerId}
                onChange={(value: string | null) => {
                  const dealerId = value || ''
                  setSettings(prev => ({ ...prev, dealerId }))
                  setSelectedRunId(null)
                  if (!dealerId) {
                    setPastRuns([])
                    setShowResults(false)
                    setJobs([])
                  }
                }}
                data={getAllDealerships()}
                searchable
                clearable
                size="sm"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
              <Radio.Group label="Evaluation scenario" value={mode} onChange={value => setMode(value as EvalMode)} size="sm">
                <Group mt="xs" gap="md">
                  <Radio value="conversation" label="Conversation" />
                  <Radio value="vehicle" label="Vehicle" />
                </Group>
              </Radio.Group>
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, md: 3 }}>
              <Checkbox.Group
                label="Channel"
                value={selectedContactChannels(settings.channels)}
                onChange={values => {
                  const next = values as EvalContactChannel[]
                  setSettings(prev => ({
                    ...prev,
                    channels: {
                      sms: next.includes('sms'),
                      email: next.includes('email'),
                    },
                  }))
                }}
                size="sm"
              >
                <Group mt="xs" gap="md">
                  <Checkbox value="sms" label="SMS" />
                  <Checkbox value="email" label="Email" />
                </Group>
              </Checkbox.Group>
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, md: 2 }}>
              <NumberInput
                label="Eval loops"
                value={settings.count}
                onChange={(value: string | number) =>
                  setSettings(prev => ({ ...prev, count: Math.min(20, Math.max(1, Number(value) || 1)) }))
                }
                min={1}
                max={20}
                size="sm"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 12 }} style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                onClick={handleRunEvaluation}
                loading={isRunning}
                leftSection={<IconScale size={16} />}
                disabled={
                  !settings.dealerId || isRunning || selectedContactChannels(settings.channels).length === 0
                }
                size="sm"
              >
                Run {settings.count === 1 ? 'loop' : `${settings.count} loops`}
              </Button>
            </Grid.Col>
          </Grid>

          {isRunning && progress.total > 0 && (
            <Stack gap={4}>
              <Group gap="xs" wrap="nowrap">
                <Progress
                  value={(progress.completed / progress.total) * 100}
                  size="sm"
                  style={{ flex: 1, minWidth: 120 }}
                  animated={progress.completed < progress.total}
                  color="blue"
                />
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {progress.completed}/{progress.total}
                  {inFlightCount > 0 ? ` · ${inFlightCount} in flight` : ''}
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                Evaluations run in parallel; results update as each completes.
              </Text>
            </Stack>
          )}
        </Stack>
      </Card>

      {settings.dealerId && (
        <Card withBorder padding="md">
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Group gap="xs">
                <IconHistory size={18} />
                <Title order={5} style={{ margin: 0 }}>
                  Past evaluation runs
                </Title>
              </Group>
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconRefresh size={14} />}
                onClick={() => void loadPastRuns()}
                loading={loadingPastRuns}
              >
                Refresh
              </Button>
            </Group>
            <Text size="xs" c="dimmed">
              The most recent saved run loads automatically. Pick another run below to compare history.
            </Text>
            {loadingPastRuns && pastRuns.length === 0 ? (
              <Group gap="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">
                  Loading past runs…
                </Text>
              </Group>
            ) : pastRuns.length === 0 ? (
              <Text size="sm" c="dimmed">
                No saved runs for this dealership and scenario yet.
              </Text>
            ) : (
              <Select
                placeholder="Select a past run to review"
                data={pastRuns.map(run => ({ value: run.runId, label: formatRunLabel(run) }))}
                value={selectedRunId}
                onChange={value => {
                  if (value) void loadHistoricalRun(value)
                }}
                searchable
                clearable
                nothingFoundMessage="No matching runs"
                rightSection={loadingRunDetails ? <Loader size="xs" /> : undefined}
                onClear={() => {
                  setSelectedRunId(null)
                  setViewingHistorical(false)
                  if (!isRunning) {
                    setShowResults(false)
                    setJobs([])
                  }
                }}
              />
            )}
          </Stack>
        </Card>
      )}

      {showResults && (
        <Card withBorder padding="md">
          <Group justify="space-between" align="flex-start" mb="xs">
            <Group gap="xs">
              <Title order={5} style={{ margin: 0 }}>
                {viewingHistorical ? 'Historical results' : 'Results'}
              </Title>
              {viewingHistorical && selectedRunId && (
                <Badge size="sm" variant="outline" color="gray">
                  {selectedRunId}
                </Badge>
              )}
              {jobs.length > 0 && (
                <>
                  <Badge size="sm" variant="light" color="blue">
                    {progress.completed}/{progress.total || jobs.length}
                  </Badge>
                  <Badge size="sm" color="green" variant="light">
                    {successCount} ok
                  </Badge>
                  <Badge size="sm" color="red" variant="light">
                    {errorCount} failed
                  </Badge>
                  {scoredJobs.length > 0 && resultsDisplayMode === 'conversation' && (
                    <Badge size="sm" color="indigo" variant="light">
                      Avg trust: {avgTrust.toFixed(1)}
                    </Badge>
                  )}
                </>
              )}
            </Group>
            {!isRunning && (
              <Button
                variant="subtle"
                size="xs"
                onClick={() => {
                  setShowResults(false)
                  setJobs([])
                  setProgress({ completed: 0, total: 0 })
                  setViewingHistorical(false)
                  setSelectedRunId(null)
                }}
              >
                Dismiss
              </Button>
            )}
          </Group>

          {jobs.length === 0 && isRunning && (
            <Text size="sm" c="dimmed">
              Starting evaluations…
            </Text>
          )}

          {jobs.length > 0 && (
            <Stack gap="sm" style={{ maxHeight: 560, overflowY: 'auto' }}>
              {jobs.map(job => (
                <Card key={job.id} withBorder padding="sm">
                  <Stack gap="xs">
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Group gap="xs" wrap="wrap" style={{ minWidth: 0 }}>
                        <Badge
                          size="sm"
                          color={
                            job.status === 'success'
                              ? 'green'
                              : job.status === 'error'
                                ? 'red'
                                : job.status === 'running'
                                  ? 'blue'
                                  : 'gray'
                          }
                          leftSection={
                            job.status === 'success' ? (
                              <IconCheck size={10} />
                            ) : job.status === 'error' ? (
                              <IconX size={10} />
                            ) : undefined
                          }
                        >
                          {job.status}
                        </Badge>
                        <Text size="sm" fw={600}>
                          {job.personaLabel}
                        </Text>
                        {job.trustScore != null && (
                          <Badge size="sm" variant="light" color="blue">
                            {job.trustScore.toFixed(1)}/100
                          </Badge>
                        )}
                        {job.outcomeAttainment && (
                          <Badge
                            size="sm"
                            variant="outline"
                            color={
                              job.outcomeAttainment === 'pass' ? 'green' : job.outcomeAttainment === 'partial' ? 'yellow' : 'red'
                            }
                          >
                            {job.outcomeAttainment}
                          </Badge>
                        )}
                      </Group>
                      <Group gap={4}>
                        {job.messageCount != null && (
                          <Badge size="xs" variant="outline">
                            {job.messageCount} msgs
                          </Badge>
                        )}
                        {job.appointmentScheduled && (
                          <Badge size="xs" variant="outline" color="green">
                            Appt
                          </Badge>
                        )}
                        {job.escalated && (
                          <Badge size="xs" variant="outline" color="orange">
                            Escalated
                          </Badge>
                        )}
                      </Group>
                    </Group>

                    {(job.customerName || job.customerId) && (
                      <Text size="xs" c="dimmed">
                        {job.customerName}
                        {job.customerId ? ` · ${job.customerId}` : ''}
                      </Text>
                    )}

                    {job.error && (
                      <Text size="xs" c="red">
                        {job.error}
                      </Text>
                    )}

                    {job.runId && resultsDisplayMode === 'vehicle' && (
                      <Text size="xs" c="dimmed">
                        Run ID: {job.runId}
                      </Text>
                    )}

                    {resultsDisplayMode === 'vehicle' && job.vehicleRows && job.vehicleRows.length > 0 && (
                      <Group gap="xs">
                        {job.vehicleRows
                          .filter(r => r.evalType.startsWith('vehicle-eval-'))
                          .map(row => (
                            <Badge key={row.id} size="xs" variant="light">
                              {row.evalType.replace('vehicle-eval-', '')}: {row.overallScore.toFixed(1)}
                            </Badge>
                          ))}
                      </Group>
                    )}

                    {job.judgeJustification && resultsDisplayMode === 'conversation' && (
                      <>
                        <Group gap="xs" style={{ cursor: 'pointer' }} onClick={() => toggleSection(job.id, 'justification')}>
                          <Text size="xs" fw={500}>
                            Justification
                          </Text>
                          {expandedSections[job.id]?.justification ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                        </Group>
                        <Collapse in={expandedSections[job.id]?.justification || false}>
                          <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                            {job.judgeJustification}
                          </Text>
                        </Collapse>
                      </>
                    )}

                    {job.subscores && job.subscores.length > 0 && resultsDisplayMode === 'conversation' && (
                      <>
                        <Group gap="xs" style={{ cursor: 'pointer' }} onClick={() => toggleSection(job.id, 'subscores')}>
                          <Text size="xs" fw={500}>
                            Dimensions
                          </Text>
                          {expandedSections[job.id]?.subscores ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                        </Group>
                        <Collapse in={expandedSections[job.id]?.subscores || false}>
                          <Table striped withTableBorder>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>Criterion</Table.Th>
                                <Table.Th>Rating</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {job.subscores.map((s, idx) => (
                                <Table.Tr key={idx}>
                                  <Table.Td>{s.criterion}</Table.Td>
                                  <Table.Td>{s.rating}/4</Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        </Collapse>
                      </>
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </Card>
      )}
    </Stack>
  )
}
